import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils/cn'
import { Avatar } from '@/components/ui/Avatar'
import { SearchBar } from '@/components/search/SearchBar'
import { useAuth } from '@/components/layout/AuthProvider'
import { supabaseClient } from '@/lib/supabase/client'
import {
  IconMenu, IconSquidsLogo, IconSearch, IconPlus,
  IconUser, IconSettings, IconLogOut, IconBarChart
} from '@/components/ui/Icons'

interface HeaderProps {
  user: {
    id: string; username: string; display_name: string
    avatar_url: string | null; is_verified: boolean; karma_score: number; role: string
  } | null
  onMenuToggle: () => void
}

export function Header({ user, onMenuToggle }: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false)
    }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const handleSignOut = async () => {
    setUserMenuOpen(false)
    await supabaseClient.auth.signOut()
    navigate('/auth/login')
  }

  return (
    <header className={cn(
      "fixed top-0 inset-x-0 z-header h-14 flex items-center gap-3 px-3 sm:px-4 header-ig transition-all duration-200",
      scrolled ? "border-b border-border" : "border-b border-transparent"
    )}>
      {/* Mobile menu */}
      <button onClick={onMenuToggle} className="lg:hidden w-10 h-10 flex items-center justify-center text-text-muted hover:text-foreground rounded-xl" aria-label="Menu">
        <IconMenu className="w-5 h-5" />
      </button>

      {/* Logo */}
      <Link to="/feed" className="flex items-center gap-2 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--ig-grad)" }}>
          <IconSquidsLogo className="w-5 h-5 text-white" />
        </div>
        <span className="hidden sm:block font-bold text-base tracking-tight">squids</span>
      </Link>

      {/* Search */}
      <div className="flex-1 max-w-[460px] mx-auto hidden md:block">
        <SearchBar />
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Mobile search */}
        <Link to="/search" className="md:hidden w-9 h-9 flex items-center justify-center text-text-muted hover:text-foreground rounded-xl">
          <IconSearch className="w-5 h-5" />
        </Link>

        {user ? (
          <>
            <Link to="/post/create" className="btn-ig hidden sm:flex items-center gap-1.5 text-sm px-3 py-1.5">
              <IconPlus className="w-4 h-4" /> Post
            </Link>

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-2 rounded-full hover:opacity-80 transition-opacity"
              >
                <div className="avatar-ring">
                  <Avatar src={user.avatar_url} alt={user.display_name} size="sm" />
                </div>
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div initial={{ opacity: 0, scale: 0.95, y: -4 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }} transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-2 w-52 bg-surface-elevated border border-border rounded-xl shadow-xl z-modal overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-semibold text-sm truncate">{user.display_name}</p>
                      <p className="text-text-muted text-xs truncate">@{user.username}</p>
                    </div>
                    <div className="py-1">
                      {[
                        { to: `/profile/${user.username}`, icon: IconUser, label: 'Profile' },
                        { to: '/dashboard', icon: IconBarChart, label: 'Dashboard' },
                        { to: '/settings', icon: IconSettings, label: 'Settings' },
                      ].map(({ to, icon: Icon, label }) => (
                        <Link key={to} to={to} onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-muted hover:bg-muted hover:text-foreground transition-colors">
                          <Icon className="w-4 h-4" />{label}
                        </Link>
                      ))}
                      <button onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-muted transition-colors">
                        <IconLogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/auth/login" className="btn-ig-outline text-sm px-3 py-1.5">Log in</Link>
            <Link to="/auth/register" className="btn-ig text-sm px-3 py-1.5">Sign up</Link>
          </div>
        )}
      </div>
    </header>
  )
}